import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Invoice } from "src/types/index";
import { apiRequest } from "src/services/apiClient";

interface PriceResult {
    pages: number;
    price: number;
}

interface Props {
    canSend: Invoice[];
    noConsent: Invoice[];
    letterSent: Invoice[];
    noAddress: Invoice[];
    cannotSend: Invoice[];
    color: boolean;
    duplex: boolean;
    envelope: string;
    dispatch: string;
    shipping: string;
}

type TabKey = "canSend" | "noConsent" | "letterSent" | "noAddress" | "skip";

function LetterRow({ inv, prices }: { inv: Invoice; prices: Record<string, PriceResult | null> }) {
    const r = inv.recipient;
    const name = r ? `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() : "—";
    const address = r
        ? [r.street, [r.plz, r.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")
        : "—";
    const price = prices[inv.id];
    return (
        <>
            <strong className="bsp-grid__id">{inv.readableId}</strong>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
                <span className="bsp-grid__info-addr" title={address}>{address}</span>
            </span>
            <span className="bsp-grid__cost-pages">
                {price === undefined
                    ? <span className="bsp-grid__spinner" />
                    : price === null
                        ? "—"
                        : `€${price.price.toFixed(2)} (${price.pages} S.)`
                }
            </span>
        </>
    );
}

function SkippedRow({ inv }: { inv: Invoice }) {
    const r = inv.recipient;
    const name = r ? `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() : "—";
    const address = r
        ? [r.street, [r.plz, r.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")
        : "";
    return (
        <>
            <strong className="bsp-grid__id">{inv.readableId}</strong>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
                {address
                    ? <span className="bsp-grid__info-addr" title={address}>{address}</span>
                    : <span className="bsp-grid__info-addr bsp-grid__info-addr--missing">—</span>
                }
            </span>
        </>
    );
}

export default function BulkLetterPreview({ canSend, noConsent, letterSent, noAddress, cannotSend, color, duplex, envelope, dispatch, shipping }: Props) {
    const { t } = useTranslation();
    const [prices, setPrices] = useState<Record<string, PriceResult | null>>({});

    useEffect(() => {
        canSend.forEach((inv) => {
            const params = new URLSearchParams({
                invoiceId:  inv.id,
                color:      color ? "1" : "0",
                duplex:     duplex ? "1" : "0",
                envelope,
                shipping,
                registered: dispatch || "",
            });
            apiRequest<PriceResult>(`/notifications/letterxpress-price?${params}`, {}, {})
                .then((data) => setPrices((prev) => ({ ...prev, [inv.id]: data })))
                .catch(() => setPrices((prev) => ({ ...prev, [inv.id]: null })));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadedCount   = canSend.filter((inv) => prices[inv.id] !== undefined).length;
    const allLoaded     = loadedCount === canSend.length;
    const someNull      = canSend.some((inv) => prices[inv.id] === null);
    const totalCost     = canSend.reduce((sum, inv) => {
        const p = prices[inv.id];
        return p ? sum + p.price : sum;
    }, 0);

    const tabs: { key: TabKey; label: string; items: Invoice[] }[] = [
        canSend.length > 0    && { key: "canSend",    label: t("invoices.bulkLetter.canSend",    { count: canSend.length }),    items: canSend },
        noConsent.length > 0  && { key: "noConsent",  label: t("invoices.bulkLetter.noConsent",  { count: noConsent.length }),  items: noConsent },
        letterSent.length > 0 && { key: "letterSent", label: t("invoices.bulkLetter.letterSent", { count: letterSent.length }), items: letterSent },
        noAddress.length > 0  && { key: "noAddress",  label: t("invoices.bulkLetter.noAddress",  { count: noAddress.length }),  items: noAddress },
        cannotSend.length > 0 && { key: "skip",       label: t("invoices.bulkLetter.cannotSend", { count: cannotSend.length }), items: cannotSend },
    ].filter(Boolean) as { key: TabKey; label: string; items: Invoice[] }[];

    const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? "canSend");
    const skipped = active !== "canSend" ? tabs.find((tab) => tab.key === active) : undefined;

    return (
        <div className="bsp bsp--letter">
            <div className="bsp__tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.key}
                        className={`bsp__tab bsp__tab--${tab.key}${active === tab.key ? " bsp__tab--active" : ""}`}
                        onClick={() => setActive(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="bsp__panel" role="tabpanel">
                {active === "canSend" && (
                    <div className="bsp-grid bsp-grid--letter">
                        <span className="bsp-grid__hdr">{t("table.id")}</span>
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("invoices.bulkLetter.cost")}</span>
                        {canSend.map((inv) => <LetterRow key={inv.id} inv={inv} prices={prices} />)}
                    </div>
                )}
                {skipped && (
                    <div className="bsp-grid bsp-grid--no-address">
                        <span className="bsp-grid__hdr">{t("table.id")}</span>
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        {skipped.items.map((inv) => <SkippedRow key={inv.id} inv={inv} />)}
                    </div>
                )}
            </div>
            {active === "canSend" && canSend.length > 0 && (
                <div className="bsp__total">
                    <span className="bsp__total-label">
                        {someNull ? t("invoices.bulkLetter.totalPartial") : t("invoices.bulkLetter.total")}
                    </span>
                    <span className="bsp__total-value">
                        {!allLoaded
                            ? <span className="bsp-grid__spinner" />
                            : `€${totalCost.toFixed(2)}`
                        }
                    </span>
                </div>
            )}
        </div>
    );
}
